import type { Employee, EmployeeDocumentKind, VentureId } from '@squadlab/contracts';

/**
 * Compliance expiry scan (P6): which tracked documents fall inside their
 * renewal lead window right now. Pure over an injected instant; the watchdog
 * command turns hits into `doc-renewal` cases (the SOPs "open themselves").
 *
 * Lead windows are OPERATIONAL SOP choices (how early we start a renewal),
 * not labour law — they live here, not in the KSA policy tables, and the HR
 * owner tunes them like any playbook content.
 */
export const COMPLIANCE_LEAD_DAYS: Readonly<Record<EmployeeDocumentKind, number>> = {
  iqama: 90,
  passport: 90,
  contract: 60,
  'medical-insurance': 45,
  certification: 30,
};

export interface ExpiringDocument {
  readonly ventureId: VentureId;
  readonly employeeId: string;
  readonly employeeName: string;
  readonly kind: EmployeeDocumentKind;
  readonly reference?: string;
  readonly expiresOn: string;
  /** Calendar days until expiry (negative = already expired). */
  readonly daysLeft: number;
  /** Stable dedupe key — one renewal case per document instance. */
  readonly key: string;
}

const MS_PER_DAY = 86_400_000;

export function watchKey(
  ventureId: VentureId,
  employeeId: string,
  kind: EmployeeDocumentKind,
  expiresOn: string,
): string {
  return `${ventureId}/${employeeId}/${kind}/${expiresOn}`;
}

/** Scan employees' documents; most urgent (fewest days left) first. */
export function scanEmployees(employees: readonly Employee[], nowMs: number): readonly ExpiringDocument[] {
  const hits: ExpiringDocument[] = [];
  for (const e of employees) {
    for (const d of e.documents) {
      const daysLeft = Math.round((Date.parse(`${d.expiresOn}T00:00:00Z`) - nowMs) / MS_PER_DAY);
      if (daysLeft > COMPLIANCE_LEAD_DAYS[d.kind]) continue;
      hits.push({
        ventureId: e.ventureId,
        employeeId: e.id,
        employeeName: e.nameEn,
        kind: d.kind,
        ...(d.reference ? { reference: d.reference } : {}),
        expiresOn: d.expiresOn,
        daysLeft,
        key: watchKey(e.ventureId, e.id, d.kind, d.expiresOn),
      });
    }
  }
  return hits.sort((a, b) => a.daysLeft - b.daysLeft);
}
